import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import React, { useState, useEffect, useMemo } from 'react';
import { arrayOf, bool, func, node, number, object, oneOf, string } from 'prop-types';
import filterHandlers from './filterHandlers';
import ListContext from './list-context';
import ListAction from './list-action';
import ListCheckbox from './list-checkbox';
import ListHeader from './list-header';
import ListItem from './list-item';
import ListRadio from './list-radio';
import { ListCollapse, ListDefault } from './list.styles';

const RenderItems = ({
  children,
  dataSource,
  renderItem,
  searchProperty,
  searchTerm,
  sortBy
}) => {
  const filteredData = useMemo(() => filterHandlers({
    dataSource,
    searchProperty,
    searchTerm,
    sortBy
  }), [dataSource, searchTerm]);
  return Array.isArray(filteredData) && renderItem instanceof Function ? filteredData == null ? void 0 : filteredData.map((item, index) => renderItem(Object.assign({
    key: index
  }, item))) : children;
};

const List = _ref => {
  let {
    as: type,
    children,
    dataSource,
    disableGutters,
    horizontal,
    divided,
    name,
    renderItem,
    scale,
    searchProperty,
    searchTerm,
    selectable,
    sortBy,
    variant,
    renderAction,
    trigger,
    collapsedIn,
    collapse
  } = _ref,
      props = _objectWithoutPropertiesLoose(_ref, ["as", "children", "dataSource", "disableGutters", "horizontal", "divided", "name", "renderItem", "scale", "searchProperty", "searchTerm", "selectable", "sortBy", "variant", "renderAction", "trigger", "collapsedIn", "collapse"]);

  const Container = collapse ? ListCollapse : ListDefault;
  const [data, setData] = useState(dataSource);
  const [term, setTerm] = useState(searchTerm);
  useEffect(() => {
    setTerm(searchTerm);
  }, [searchTerm]);
  useEffect(() => {
    setData(dataSource);
  }, [JSON.stringify(dataSource)]);
  return /*#__PURE__*/React.createElement(ListContext, {
    value: {
      divided,
      disableGutters,
      horizontal,
      name,
      selectable,
      scale,
      variant,
      type,
      isCollapsed: trigger
    }
  }, /*#__PURE__*/React.createElement(Container, _extends({
    forwardedAs: type,
    divided: divided,
    trigger: trigger,
    collapsedIn: collapsedIn,
    horizontal: horizontal,
    "data-testid": "list"
  }, props), /*#__PURE__*/React.createElement(RenderItems, {
    searchTerm: term,
    dataSource: data,
    sortBy: sortBy,
    searchProperty: searchProperty,
    renderItem: renderItem,
    children: children
  })), renderAction);
};

process.env.NODE_ENV !== "production" ? List.propTypes = {
  as: string,
  children: node,
  dataSource: arrayOf(object),
  disableGutters: bool,
  divided: oneOf([true, false, 'inset']),
  name: string,
  renderItem: func,
  scale: oneOf(['small', 'medium', 'large']),
  searchProperty: string,
  searchTerm: string,
  selectable: bool,
  variant: oneOf(['light', 'primary', 'transparent', 'base']),
  sortBy: func,
  renderAction: node,
  trigger: bool,
  collapsedIn: number,
  collapse: bool,
  horizontal: bool
} : void 0;
List.defaultProps = {
  as: 'ul',
  children: null,
  dataSource: [],
  disableGutters: false,
  divided: false,
  scale: 'medium',
  searchProperty: '',
  searchTerm: '',
  selectable: false,
  variant: 'transparent',
  horizontal: false
};

List.Collapse = props => /*#__PURE__*/React.createElement(List, _extends({
  collapse: true
}, props));

List.Header = ListHeader;
List.Item = ListItem;
List.Action = ListAction;
List.Checkbox = ListCheckbox;
List.Radio = ListRadio;
List.Collapse.displayName = 'ListCollapse';
export default List;